import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import ContactButton from './ContactButton';
import { format, isAfter, parseISO } from 'date-fns';
import { Vehicle, VehicleList } from '../types/api';
import { convertToBgn, convertToEur } from '../utils/currency';
import { generateVehicleSlug } from '../utils/slugify';
import { 
  ClockIcon, 
  ExclamationTriangleIcon,
  CurrencyDollarIcon
} from '@heroicons/react/24/outline';

interface VehicleCardProps {
  vehicle: Vehicle | VehicleList;
  showCountdown?: boolean;
}

const VehicleCard: React.FC<VehicleCardProps> = ({ vehicle, showCountdown = true }) => {
  const [timeRemaining, setTimeRemaining] = useState<string | null>(null);
  
  // Type guard to determine if this is VehicleList or Vehicle
  const isVehicleList = (v: Vehicle | VehicleList): v is VehicleList => {
    return 'main_image_url' in v;
  };
  
  let hasAuctionData: boolean;
  let auctionData: any = null;
  let mainImage: string | undefined;
  let isActive: boolean;
  let auctionDate: string | null;
  let currentBid: number | null;
  let buyNowPrice: number | null;
  let isBuyNow: boolean;
  
  if (isVehicleList(vehicle)) {
    // Handle VehicleList - create a mock auction object
    hasAuctionData = true;
    auctionData = {
      auction_status: vehicle.auction_status,
      auction_date: vehicle.auction_date,
      current_bid: vehicle.current_bid,
      is_featured: false,
      is_buy_now: vehicle.is_buy_now || false,
      buy_now_price: vehicle.buy_now_price || null
    };
    mainImage = vehicle.main_image_url;
    isActive = vehicle.auction_status === 'active';
    auctionDate = vehicle.auction_date || null;
    currentBid = vehicle.current_bid || null;
    buyNowPrice = vehicle.buy_now_price || null;
    isBuyNow = vehicle.is_buy_now || false;
  } else {
    // Handle full Vehicle
    hasAuctionData = !!vehicle.auction_data;
    auctionData = vehicle.auction_data;
    mainImage = vehicle.images?.[0]?.image_url;
    isActive = hasAuctionData && auctionData?.auction_status === 'active';
    auctionDate = hasAuctionData ? auctionData?.auction_date || null : null;
    currentBid = hasAuctionData ? auctionData?.current_bid || null : null;
    buyNowPrice = hasAuctionData ? auctionData?.buy_now_price || null : null;
    isBuyNow = hasAuctionData ? auctionData?.is_buy_now || false : false;
  }
  
  // For compatibility with the rest of the component
  const hasAuction = auctionData;

  // Convert auction date from EDT to Sofia timezone
  const getSofiaTime = (dateString: string) => {
    try {
      // The date from backend is naive (no timezone info) but represents EDT time
      // We need to parse it as EDT (UTC-4) and convert to Sofia (UTC+3)
      const date = parseISO(dateString);
      
      // Get browser's timezone offset in minutes
      const browserOffset = new Date().getTimezoneOffset(); // negative if ahead of UTC
      
      // EDT offset from UTC in minutes: -4 hours = -240 minutes
      const edtOffset = -240;
      
      // Sofia offset from UTC in minutes: +3 hours = -180 minutes (negative because of getTimezoneOffset)
      const sofiaOffset = -180;
      
      // Calculate the adjustment needed:
      // 1. Remove browser's interpretation (add back browser offset)
      // 2. Apply EDT offset (subtract EDT offset to get UTC)
      // 3. Apply Sofia offset (add Sofia offset from UTC)
      const adjustment = (browserOffset - edtOffset - sofiaOffset) * 60 * 1000;
      
      const sofiaTime = new Date(date.getTime() + adjustment);
      
      return sofiaTime;
    } catch (error) {
      return null;
    }
  };

  // Format countdown for ending soon with Sofia timezone
  const getTimeRemaining = () => {
    if (!auctionDate) return null;
    
    try {
      const sofiaEndTime = getSofiaTime(auctionDate);
      if (!sofiaEndTime) return null;
      
      // Get current time in Sofia timezone
      const now = new Date();
      // Current time is already in browser's timezone, no adjustment needed for comparison
      const sofiaNow = now;
      
      if (isAfter(sofiaNow, sofiaEndTime)) {
        return 'Приключил';
      }
      
      const diff = sofiaEndTime.getTime() - sofiaNow.getTime();
      const days = Math.floor(diff / (1000 * 60 * 60 * 24));
      const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((diff % (1000 * 60)) / 1000);
      
      if (days > 0) {
        return `${days}д ${hours}ч ${minutes}м`;
      } else if (hours > 0) {
        return `${hours}ч ${minutes}м ${seconds}с`;
      } else if (minutes > 0) {
        return `${minutes}м ${seconds}с`;
      } else {
        return `${seconds}с`;
      }
    } catch (error) {
      return null;
    }
  };

  // Update countdown every second
  useEffect(() => {
    if (!showCountdown || !auctionDate) {
      setTimeRemaining(null);
      return;
    }

    const updateCountdown = () => {
      const remaining = getTimeRemaining();
      setTimeRemaining(remaining);
    };

    updateCountdown();
    const interval = setInterval(updateCountdown, 1000);

    return () => clearInterval(interval);
  }, [showCountdown, auctionDate]);

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden hover:shadow-md transition-shadow duration-200">
      <Link to={`/vehicles/${generateVehicleSlug(vehicle)}`}>
        {/* Image */}
        <div className="relative h-48 bg-gray-200">
          {mainImage ? (
            <img
              src={mainImage}
              alt={`${vehicle.year} ${vehicle.make} ${vehicle.model}`}
              className="w-full h-full object-cover"
              loading="lazy"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-gray-400">
              <svg className="w-12 h-12" fill="currentColor" viewBox="0 0 24 24">
                <path d="M21 16.5c0 .38-.21.71-.53.88l.01.12c0 1.1-.9 2-2 2s-2-.9-2-2c0-.12.01-.23.03-.34C16.18 17.5 15.62 18 15 18s-1.18-.5-1.51-.84c.02.11.03.22.03.34 0 1.1-.9 2-2 2s-2-.9-2-2c0-.12.01-.23.03-.34C9.18 17.5 8.62 18 8 18s-1.18-.5-1.51-.84c.02.11.03.22.03.34 0 1.1-.9 2-2 2s-2-.9-2-2l.01-.12c-.32-.17-.53-.5-.53-.88V9.5c0-.83.67-1.5 1.5-1.5S6 8.67 6 9.5V10h12V9.5c0-.83.67-1.5 1.5-1.5s1.5.67 1.5 1.5v7zm-2 0V12H5v4.5c.31-.13.64-.5 1-.5s.69.37 1 .5V14h2v2.5c.31-.13.64-.5 1-.5s.69.37 1 .5V14h2v2.5c.31-.13.64-.5 1-.5s.69.37 1 .5V14h2v2.5c.31-.13.64-.5 1-.5s.69.37 1 .5z"/>
              </svg>
            </div>
          )}
          
          {/* Status badges */}
          <div className="absolute top-2 left-2 flex flex-col gap-1">
            {/* Source badge */}
            {vehicle.source && (
              <span className={`text-xs font-medium px-2 py-1 rounded-full ${
                vehicle.source === 'IAAI_CANADA' ? 'bg-blue-100 text-blue-800' :
                vehicle.source === 'IAAI_USA' ? 'bg-purple-100 text-purple-800' :
                vehicle.source === 'COPART' ? 'bg-orange-100 text-orange-800' :
                'bg-gray-100 text-gray-800'
              }`}>
                {vehicle.source === 'IAAI_CANADA' ? 'IAAI 🇨🇦' :
                 vehicle.source === 'IAAI_USA' ? 'IAAI 🇺🇸' :
                 vehicle.source === 'COPART' ? 'Copart 🇺🇸' :
                 vehicle.source}
              </span>
            )}
            {vehicle.is_sold && (
              <span className="bg-red-100 text-red-800 text-xs font-medium px-2 py-1 rounded-full">
                Продаден
              </span>
            )}
            {isBuyNow && !vehicle.is_sold && (
              <span className="bg-green-100 text-green-800 text-xs font-medium px-2 py-1 rounded-full">
                Купи сега
              </span>
            )}
            {hasAuction?.is_featured && (
              <span className="bg-yellow-100 text-yellow-800 text-xs font-medium px-2 py-1 rounded-full">
                Препоръчан
              </span>
            )}
          </div>

          {/* Countdown timer */}
          {timeRemaining && timeRemaining !== 'Приключил' && (
            <div className="absolute top-2 right-2">
              <div className="bg-red-100 text-red-800 text-xs font-medium px-2 py-1 rounded-full flex items-center">
                <ClockIcon className="w-3 h-3 mr-1" />
                {timeRemaining}
              </div>
            </div>
          )}
        </div>

        {/* Content */}
        <div className="p-4">
          {/* Title */}
          <h3 className="text-base font-semibold text-gray-900 mb-2">
            {vehicle.year} {vehicle.make} {vehicle.model}
          </h3>

          {/* Details */}
          <div className="space-y-2 mb-4">
            {/* Odometer */}
            <div className="flex items-center text-sm text-gray-600">
              <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              Пробег: {vehicle.odometer ? `${vehicle.odometer.toLocaleString()} ${vehicle.odometer_unit || 'км'}` : 'Няма инфо'}
            </div>


            {/* Damage */}
            {vehicle.primary_damage && (
              <div className="flex items-center text-sm text-gray-600">
                <ExclamationTriangleIcon className="w-4 h-4 mr-1" />
                Щети: {vehicle.primary_damage}
              </div>
            )}

            {/* Auction Date in Sofia Time */}
            {auctionDate && (
              <div className="flex items-center text-sm text-gray-600">
                <ClockIcon className="w-4 h-4 mr-1" />
                Търг: {getSofiaTime(auctionDate) ? 
                  format(getSofiaTime(auctionDate)!, 'dd.MM.yyyy HH:mm') + ' (София)'
                  : format(parseISO(auctionDate), 'dd.MM.yyyy')
                }
              </div>
            )}
          </div>

          {/* Pricing - Prioritize Buy Now Price */}
          {buyNowPrice && buyNowPrice > 0 ? (
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <CurrencyDollarIcon className="w-5 h-5 mr-1 text-green-600" />
                <span className="text-xl font-bold text-green-600">
                  {convertToBgn(buyNowPrice, vehicle.country).toLocaleString()} лв
                </span>
                <span className="text-xs text-gray-500 ml-2">
                  ({convertToEur(buyNowPrice, vehicle.country).toLocaleString()} €)
                </span>
              </div>
              <span className="text-sm font-medium text-green-600">Купи сега</span>
            </div>
          ) : currentBid ? (
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <span className="text-lg font-semibold text-primary-600">
                  {convertToBgn(currentBid, vehicle.country).toLocaleString()} лв
                </span>
                <span className="text-xs text-gray-500 ml-2">
                  ({convertToEur(currentBid, vehicle.country).toLocaleString()} €)
                </span>
              </div>
              <span className="text-sm text-gray-500">Текуща оферта</span>
            </div>
          ) : null}
        </div>
      </Link>
      
      {/* Contact Button */}
      <div className="p-4 bg-gray-50">
        <ContactButton 
          vehicleId={vehicle.id?.toString()} 
          vehicleName={`${vehicle.year} ${vehicle.make} ${vehicle.model}`}
          variant="secondary"
          className="w-full"
        />
      </div>
    </div>
  );
};

export default VehicleCard;
